export type MeasuredBox = {
	x: number;
	y: number;
	width: number;
	height: number;
};

const INK_FREE_FILLS = new Set(['none', 'transparent', 'white', '#fff', '#ffffff', 'rgb(255, 255, 255)', 'rgb(255,255,255)']);
const INK_FREE_STROKES = new Set(['none', 'transparent', '', 'rgba(0, 0, 0, 0)']);

const INK_SELECTOR = 'text, line, path, ellipse, rect, use, image, polygon, polyline, circle';
const NON_RENDERED_PARENTS = 'defs, symbol, pattern, clipPath, mask, marker';

// True when the element paints nothing visible on the white page: opacity 0,
// hidden, or a white/none fill with no stroke. Such elements (appliance hit-area
// polygons, above-counter extents, cutout masks) must not inflate the framing.
const paintsNoInk = (el: SVGGraphicsElement): boolean => {
	const style = window.getComputedStyle(el);
	if (style.opacity === '0' || style.visibility === 'hidden') return true;
	const fill = (el.getAttribute('fill') ?? style.fill ?? '').toLowerCase();
	const stroke = (el.getAttribute('stroke') ?? style.stroke ?? '').toLowerCase();
	// A zero stroke-width paints nothing regardless of color (e.g. a TOP_ONLY item's invisible depth-hint outline in render-panel-item.tsx)
	const strokeWidth = parseFloat(el.getAttribute('stroke-width') ?? style.strokeWidth ?? '');
	const strokeIsInk = !INK_FREE_STROKES.has(stroke) && !(Number.isFinite(strokeWidth) && strokeWidth === 0);
	return INK_FREE_FILLS.has(fill) && !strokeIsInk;
};

/**
 * Union bbox of every ink-painting leaf element inside `group`, expressed in the
 * group's own coordinates. Rotated elements contribute their transformed
 * axis-aligned box, so a sideways dimension label counts as the space it really
 * occupies. Only genuinely unpainted things are skipped -- this is a visual fit,
 * so whatever a reader can see has to be inside the frame.
 * @param group the content group whose rendered ink should be measured
 */
export const measureVisibleBBox = (group: SVGGElement): MeasuredBox | null => {
	const groupScreenCTM = group.getScreenCTM();
	if (!groupScreenCTM) return null;
	const toGroup = groupScreenCTM.inverse();

	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	group.querySelectorAll<SVGGraphicsElement>(INK_SELECTOR).forEach((el) => {
		if (el.closest(NON_RENDERED_PARENTS)) return;
		if (paintsNoInk(el)) return;

		const elCTM = el.getScreenCTM();
		if (!elCTM) return;

		let box: DOMRect | null = null;
		try {
			box = el.getBBox();
		} catch (_error) {
			return;
		}
		if (!box) return;
		if (box.width === 0 && box.height === 0) return;

		const matrix = toGroup.multiply(elCTM);
		const corners: Array<[number, number]> = [
			[box.x, box.y],
			[box.x + box.width, box.y],
			[box.x, box.y + box.height],
			[box.x + box.width, box.y + box.height]
		];

		corners.forEach(([px, py]) => {
			const point = new DOMPoint(px, py).matrixTransform(matrix);
			minX = Math.min(minX, point.x);
			maxX = Math.max(maxX, point.x);
			minY = Math.min(minY, point.y);
			maxY = Math.max(maxY, point.y);
		});
	});

	if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};
